import { View, Pressable, Alert, TextInput as RNTextInput } from "react-native"
import { Controller, useForm, useFieldArray, useWatch, type Control, type UseFieldArrayUpdate } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { router } from "expo-router"
import { useState, useRef, useEffect, useMemo } from "react"
import { generateWeightBarcode } from "@white-labels/utils"

import { Header } from "@src/components/Header"
import { trpc } from "@src/utils/trpc"
import { ar } from "@src/i18n/ar"
import { analytics } from "@src/utils/analytics"
import { TextInput, Text, Button, MCIcon, ModalWrapper } from "@src/components/core"
import { KeyboardAwareScrollView } from "@src/components/keyboard-aware-scroll-view"
import { UpcInput } from "@src/components/upc-input"
import { ReceiptPicker } from "@src/components/receipt-picker"
import { OrderPicker } from "@src/components/order-picker"
import { formatPrice, getPaymentLabel, sharedStyles } from "@src/utils"
import { SafeAreaView } from "react-native-safe-area-context"

type Mode = "standalone" | "receipt" | "order"

/** A return line is "linked" (receipt/order) when `unitPrice` is set; weight presence marks a weight product. */
type FormItem = {
  productId: number
  upc: string
  name: string
  quantity: number
  price: number
  maxQuantity?: number
  weight?: number
  unitPrice?: number
}

type FormValues = {
  refundMethod: "cash" | "card" | "wallet"
  reason: string
  items: FormItem[]
}

/** Refund for a line. Linked lines refund at their stored unit price; standalone compute from price + weight. */
function computeLineRefund(field: FormItem): number {
  if (field.unitPrice !== undefined) return field.unitPrice * field.quantity
  if (field.weight !== undefined) return Math.round((field.price * field.weight * field.quantity) / 1000)
  return field.price * field.quantity
}

export default function NewReturn() {
  const utils = trpc.useUtils()
  const processReturn = trpc.returns.processReturn.useMutation()

  const [mode, setMode] = useState<Mode>("standalone")
  const [receiptId, setReceiptId] = useState<number | undefined>()
  const [orderId, setOrderId] = useState<number | undefined>()
  const [weightModalData, setWeightModalData] = useState<{ itemIndex: number; currentWeight: number } | null>(null)

  const { control, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(
      z.object({
        refundMethod: z.enum(["cash", "card", "wallet"]),
        reason: z.string().min(1),
        items: z.array(
          z.object({
            productId: z.number(),
            upc: z.string(),
            name: z.string(),
            quantity: z.number(),
            price: z.number(),
            maxQuantity: z.number().optional(),
            weight: z.number().optional(),
            unitPrice: z.number().optional(),
          }),
        ),
      }),
    ),
    defaultValues: { refundMethod: "cash" as const, reason: "", items: [] },
  })

  const { fields, append, remove, update, replace } = useFieldArray({ control, name: "items" })

  const receipt = trpc.receipts.get.useQuery({ id: receiptId! }, { enabled: !!receiptId })
  const order = trpc.orders.get.useQuery({ id: orderId! }, { enabled: !!orderId })

  const loading = processReturn.isPending
  const cartEmpty = fields.every((f) => f.quantity <= 0)

  // Linked modes (receipt/order): pre-fill every source line at qty 0 so the user just bumps
  // the quantity on the lines they want to return. Standalone starts empty.
  const sourceItems = useMemo<FormItem[]>(() => {
    const toLine = (i: { productId: number; upc: string; name: string; quantity: number }, price: number, total: number, weight?: number | null): FormItem => ({
      productId: i.productId,
      upc: i.upc,
      name: i.name,
      quantity: 0,
      price,
      maxQuantity: i.quantity,
      unitPrice: Math.round(total / i.quantity),
      ...(weight ? { weight: Math.round(weight / i.quantity) } : {}),
    })
    if (mode === "receipt" && receipt.data) return receipt.data.items.map((i) => toLine(i, i.price, i.total, i.weight))
    if (mode === "order" && order.data) return order.data.items.map((i) => toLine(i, i.unitPrice, i.lineTotal))
    return []
  }, [mode, receipt.data, order.data])

  useEffect(() => {
    if (mode !== "standalone") replace(sourceItems)
  }, [mode, sourceItems, replace])

  function handleModeChange(newMode: Mode) {
    setMode(newMode)
    setReceiptId(undefined)
    setOrderId(undefined)
    replace([])
  }

  async function handleUpcScan(upc: string) {
    if (!upc || upc.length < 3) return
    try {
      let upcToFetch = upc
      if (upc.startsWith("20") && upc.length === 7) upcToFetch = generateWeightBarcode(upc, 1000)
      const product = await utils.products.getByUpc.fetch({ upc: upcToFetch })
      const isWeight = product.type === "WEIGHT_PRODUCT"

      const existingIdx = fields.findIndex((f) => f.upc === upcToFetch)
      if (existingIdx >= 0) {
        update(existingIdx, { ...fields[existingIdx], quantity: fields[existingIdx].quantity + 1 })
      } else {
        append({
          productId: product.id,
          upc: upcToFetch,
          name: product.name,
          quantity: 1,
          price: product.price,
          ...(isWeight ? { weight: 1000 } : {}),
        })
      }
    } catch {
      Alert.alert(ar.error, ar.productNotFound)
    }
  }

  function handleWeightUpdate(newWeightInGrams: number) {
    if (!weightModalData) return
    const idx = weightModalData.itemIndex
    if (idx >= 0 && idx < fields.length) update(idx, { ...fields[idx], weight: newWeightInGrams })
    setWeightModalData(null)
  }

  const onSubmit = handleSubmit((formFields) => {
    const selectedItems = fields.filter((f) => f.quantity > 0)
    if (selectedItems.length === 0) return
    const items = selectedItems.map((f) => ({
      productId: f.productId,
      upc: f.upc,
      returnQuantity: f.quantity,
      ...(f.weight !== undefined ? { returnWeight: f.weight } : {}),
    }))
    processReturn.mutate(
      {
        ...formFields,
        items,
        ...(receiptId !== undefined ? { receiptId } : {}),
        ...(orderId !== undefined ? { orderId } : {}),
      },
      {
        onSuccess: () => {
          utils.returns.list.invalidate()
          const total = selectedItems.reduce((sum, f) => sum + computeLineRefund(f), 0)
          analytics.logEvent({ name: "return_created", params: { total } })
          router.back()
        },
        onError: (err) => Alert.alert(ar.error, err.message),
      },
    )
  })

  return (
    <>
      <Header title={ar.newReturn} />
      <KeyboardAwareScrollView className="w-full max-w-content mx-auto pt-2" contentContainerStyle={sharedStyles.contentContainer} keyboardShouldPersistTaps="handled">
        <Text className="font-bold mb-2">{ar.selectReturnType}</Text>
        <View className="flex-row gap-x-2 mb-2">
          {(
            [
              { key: "standalone" as const, label: ar.standalone },
              { key: "receipt" as const, label: ar.receiptLinked },
              { key: "order" as const, label: ar.linkedOrder },
            ] as const
          ).map(({ key, label }) => (
            <Pressable key={key} onPress={() => handleModeChange(key)} className={`flex-1 py-2 items-center rounded ${mode === key ? "bg-blue-500" : "bg-gray-200"}`}>
              <Text className={mode === key ? "text-white" : "text-black"}>{label}</Text>
            </Pressable>
          ))}
        </View>

        {mode === "receipt" && <ReceiptPicker id={receiptId} onChange={setReceiptId} className="mb-2" />}
        {mode === "order" && <OrderPicker orderId={orderId} onChange={setOrderId} className="mb-2" />}
        {mode === "standalone" && <UpcInput onValueChange={handleUpcScan} withSearch className="mb-2" />}

        <Text className="font-bold mb-2">{ar.returnItems}</Text>
        <ItemsList fields={fields} update={update} remove={remove} onWeightClick={(idx, currentWeight) => setWeightModalData({ itemIndex: idx, currentWeight })} />

        <Text className="font-bold mb-2">{ar.refundMethod}</Text>
        <Controller
          control={control}
          name="refundMethod"
          render={({ field: { onChange, value } }) => (
            <View className="flex-row gap-x-2 mb-2">
              {(["cash", "card", "wallet"] as const).map((method) => (
                <Pressable key={method} onPress={() => onChange(method)} className={`flex-1 py-2 items-center rounded ${value === method ? "bg-blue-500" : "bg-gray-200"}`}>
                  <Text className={value === method ? "text-white" : "text-black"}>{getPaymentLabel(method)}</Text>
                </Pressable>
              ))}
            </View>
          )}
        />

        <Text className="font-bold mb-2">{ar.returnReason}</Text>
        <Controller
          control={control}
          name="reason"
          render={({ field: { onChange, onBlur, value } }) => (
            <TextInput className="mb-2" placeholder={ar.enterReturnReason} onBlur={onBlur} onChangeText={onChange} value={value} multiline />
          )}
        />

        <RefundTotal control={control} />

        <Button loading={loading} disabled={cartEmpty || !formState.isValid || loading} onPress={onSubmit} className="mb-4">
          {ar.save}
        </Button>
        <SafeAreaView edges={["bottom"]} />

        {weightModalData && <WeightModal currentWeight={weightModalData.currentWeight} onConfirm={handleWeightUpdate} onClose={() => setWeightModalData(null)} />}
      </KeyboardAwareScrollView>
    </>
  )
}

function ItemsList({
  fields,
  update,
  remove,
  onWeightClick,
}: {
  fields: Array<FormItem & { id: string }>
  update: UseFieldArrayUpdate<FormValues, "items">
  remove: (index: number) => void
  onWeightClick: (index: number, currentWeight: number) => void
}) {
  if (fields.length === 0) return <Text className="text-gray-400 mb-2">{ar.selectProduct}</Text>

  return (
    <View className="mb-2">
      {fields.map((field, idx) => {
        const isLinked = field.unitPrice !== undefined
        const isWeight = field.weight !== undefined
        const weightGrams = field.weight ?? 0
        const lineTotal = computeLineRefund(field)
        const maxQty = field.maxQuantity
        const atMaxQty = maxQty !== undefined && field.quantity >= maxQty
        const atMinQty = field.quantity <= 0

        const decrement = () => {
          if (isLinked && atMinQty) return
          if (!isLinked && field.quantity <= 1) return remove(idx)
          update(idx, { ...field, quantity: field.quantity - 1 })
        }

        return (
          <View key={field.id} className="flex-row justify-between items-center p-3 mb-2 rounded-lg bg-gray-50 border border-gray-200">
            <View className="flex-1">
              <Text className="font-medium text-gray-900">{field.name}</Text>
              {isWeight ? (
                <>
                  <Pressable
                    onPress={isLinked ? undefined : () => onWeightClick(idx, weightGrams)}
                    className={`self-start px-2 py-0.5 rounded-full mt-1 ${isLinked ? "bg-gray-200" : "bg-blue-100 active:bg-blue-200"}`}
                  >
                    <Text className={`text-xs font-medium ${isLinked ? "text-gray-600" : "text-blue-700"}`}>
                      {(weightGrams / 1000).toFixed(2)} {ar.kg}
                    </Text>
                  </Pressable>
                  <Text className="text-xs text-gray-500 mt-0.5">
                    {formatPrice(field.price)} / {ar.kg} = {formatPrice(lineTotal)} {ar.egp}
                  </Text>
                </>
              ) : (
                <Text className="text-xs text-gray-500">
                  {formatPrice(field.unitPrice ?? field.price)} × {field.quantity} = {formatPrice(lineTotal)} {ar.egp}
                </Text>
              )}
            </View>
            <View className="flex-row items-center gap-2">
              <Pressable
                onPress={decrement}
                disabled={isLinked && atMinQty}
                className={`w-8 h-8 rounded-full items-center justify-center ${isLinked && atMinQty ? "bg-gray-100" : "bg-gray-200"}`}
              >
                <MCIcon name="minus" size={16} color={isLinked && atMinQty ? "#D1D5DB" : "#374151"} />
              </Pressable>
              <Text className="font-bold w-8 text-center">{field.quantity}</Text>
              <Pressable
                onPress={() => !atMaxQty && update(idx, { ...field, quantity: field.quantity + 1 })}
                className={`w-8 h-8 rounded-full items-center justify-center ${atMaxQty ? "bg-gray-100" : "bg-blue-500"}`}
              >
                <MCIcon name="plus" size={16} color={atMaxQty ? "#9CA3AF" : "white"} />
              </Pressable>
              {!isLinked && (
                <Pressable onPress={() => remove(idx)} className="ml-2 p-1">
                  <MCIcon name="close" size={18} color="#EF4444" />
                </Pressable>
              )}
            </View>
          </View>
        )
      })}
    </View>
  )
}

function RefundTotal({ control }: { control: Control<FormValues> }) {
  const fields = useWatch({ control, name: "items" }) as Array<FormItem> | undefined
  if (!fields || fields.length === 0) return null

  const totalRefund = fields.reduce((sum, field) => sum + computeLineRefund(field), 0)

  return (
    <View className="flex-row justify-between mb-2 py-2 border-t border-gray-300">
      <Text className="font-bold text-lg">{ar.refundTotal}</Text>
      <Text className="font-bold text-lg text-green-600">
        {formatPrice(totalRefund)} {ar.egp}
      </Text>
    </View>
  )
}

function WeightModal({ currentWeight, onConfirm, onClose }: { currentWeight: number; onConfirm: (grams: number) => void; onClose: () => void }) {
  const [weight, setWeight] = useState(currentWeight.toString())
  const inputRef = useRef<React.ElementRef<typeof RNTextInput>>(null)

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  function handleConfirm() {
    const weightNum = parseInt(weight, 10)
    if (weightNum > 0 && weightNum <= 99999) onConfirm(weightNum)
  }

  return (
    <ModalWrapper onClose={onClose}>
      <View className="flex-row items-center justify-between mb-4">
        <Text className="text-lg font-bold text-gray-900">{ar.enterWeight}</Text>
        <Pressable onPress={onClose} className="p-2">
          <MCIcon name="close" size={22} color="#6B7280" />
        </Pressable>
      </View>

      <Text className="text-sm font-medium text-gray-700 mb-2">{ar.weightInGrams}</Text>
      <TextInput
        ref={inputRef}
        keyboardType="number-pad"
        value={weight}
        onChangeText={setWeight}
        onSubmitEditing={handleConfirm}
        returnKeyType="done"
        className="text-center text-2xl mb-2"
        placeholder="0"
      />
      <Text className="text-sm text-gray-500 text-center mb-4">{ar.grams} (1-99999)</Text>

      <Pressable className="py-2 bg-blue-600 rounded items-center active:opacity-90" onPress={handleConfirm}>
        <Text className="text-white font-semibold">{ar.updateWeight}</Text>
      </Pressable>
    </ModalWrapper>
  )
}
